import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import Link from 'components/Link';
import { RouteContext } from 'lib/ContextTypes/route';
import { isValidUrl } from 'lib/urlUtils';

import styles from './styles.module.scss';

const SCROLL_BUFFER = 15;

/**
 * @param {object} props - The component props.
 * @param {import('lib/graphqlTypeDefs.mjs').Headline} props.headline - The headline object for the main link.
 * @param {string} props.externalUrl - The external URL for the main link.
 * @param {import('lib/graphqlTypeDefs.mjs').CuratedItemsPagedResults} props.content - The content object containing additional links.
 */
function EventDayRecirc({
  headline,
  externalUrl,
  content: {
    items = [],
  },
}) {
  const scrollRef = useRef();
  const [{
    hideLeftScroll,
    hideRightScroll,
  }, setScrollState] = useState({ hideLeftScroll: true, hideRightScroll: false });
  const { path } = useContext(RouteContext);

  if (items.length === 0) {
    return null;
  }

  const links = [{
    headline: headline.primary,
    url: externalUrl,
  }, ...items.map((item) => {
    if (item.type === 'article') {
      return {
        headline: item.headline.primary,
        url: item.url.primary,
      };
    }
    if (item.type === 'externalLink') {
      return {
        headline: item.text,
        url: item.url.primary,
      };
    }
    return null;
  })].filter(Boolean).map((item) => {
    let pathName = item.url;
    try {
      pathName = new URL(item.url).pathname;
    } catch {
      // ignore
    }
    return {
      ...item,
      active: pathName === path,
    };
  });

  let resizeTimeout;

  /**
 * Handles window resize events for the scroll container.
 * Debounces the resize event and updates the scroll state to show or hide
 * the right scroll button based on whether the content overflows horizontally.
 */
  const onResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      setScrollState({
        hideLeftScroll,
        hideRightScroll: scrollRef.current.scrollWidth === scrollRef.current.clientWidth,
      });
    }, 100);
  };

  /**
 * Handles horizontal scroll events for the scroll container.
 * Updates the scroll state to show or hide left/right scroll buttons
 * based on the current scroll position.
 *
 * @param {React.UIEvent<HTMLDivElement>} e - The scroll event object.
 */
  const onScroll = (e) => {
    const container = e.target;
    // When towards the left
    if (container.scrollLeft < SCROLL_BUFFER) {
      return setScrollState({
        hideLeftScroll: true,
        hideRightScroll: false,
      });
    }

    // When reached towards the end on the right
    if ((container.offsetWidth + container.scrollLeft + SCROLL_BUFFER) >= container.scrollWidth) {
      return setScrollState({
        hideLeftScroll: false,
        hideRightScroll: true,
      });
    }

    // When scrolling in between
    return setScrollState({
      hideLeftScroll: false,
      hideRightScroll: false,
    });
  };

  useEffect(() => {
    setScrollState({
      hideLeftScroll: true,
      hideRightScroll: scrollRef.current.scrollWidth === scrollRef.current.clientWidth,
    });

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div
      className={styles.container}
      data-activity-map="event-day-recirc-header"
      role="menu"
      data-testid="event-day-recirc"
    >
      <div
        className={styles.topBar}
      >
        <button
          aria-label="Left Scroll Button"
          title="Left Scroll Button"
          type="button"
          onClick={() => {
            const { current: scrollContainer } = scrollRef;
            scrollContainer.scrollTo({
              behavior: 'smooth',
              left: scrollContainer.scrollLeft - scrollContainer.clientWidth,
            });
          }}
          className={
            classNames(styles.leftScroll, {
              [styles.hide]: hideLeftScroll,
            })
          }
        >
          <span className="icon icon-chevron-left" />
        </button>
        <div
          className={styles.scrollContainer}
          onScroll={onScroll}
          ref={scrollRef}
          role="navigation"
        >
          {links.map(((item, i) => (isValidUrl(item.url) ? (
            <Link
              className={classNames(styles.link, {
                [styles.topic]: i === 0,
                [styles.current]: item.active,
              })}
              to={item.url}
              key={item.url}
              icid={`eventday_work_${i + 1}`}
            >
              <span className={styles.linkText}>{item.headline}</span>
            </Link>
          ) : (
            <span
              className={classNames(styles.edrText, {
                [styles.topic]: i === 0,
              })}
            >
              <span>{item.headline}</span>
            </span>
          ))))}
        </div>
        <button
          type="button"
          aria-label="Right Scroll Button"
          title="Right Scroll Button"
          onClick={() => {
            const { current: scrollContainer } = scrollRef;
            scrollContainer.scrollTo({
              behavior: 'smooth',
              left: scrollContainer.scrollLeft + scrollContainer.clientWidth,
            });
          }}
          className={
            classNames(styles.rightScroll, {
              [styles.hide]: hideRightScroll,
            })
          }
        >
          <span className="icon icon-chevron-right" />
        </button>
      </div>
    </div>
  );
}

export { EventDayRecirc };
